import type { CardDef } from './card';
import type { CardTitle } from './ids';
import type { Lore } from './lore';
import { getLang } from '../i18n';

/**
 * 卡牌**内容**的本地化取值。
 *
 * `CardDef` 里 `name` / `description` / `lore` 是中文基准，
 * `nameEn` / `descriptionEn` / `loreEn` 是可选英文。英文缺失时回退中文，
 * 这样测试 fixture 和第三方包不必强制提供英文。
 *
 * 逻辑层（core/reducer 等）**不许**读这些函数——它们只给 UI 用，
 * 保证规则层与语言无关、可重放。这里只依赖 `i18n` 的模块内全局语言，
 * 不碰 window / localStorage。
 */

/** 显示用牌名：en 取 `nameEn`，缺省回退中文 `name` */
export function cardName(card: CardDef): string {
  return getLang() === 'en' ? (card.nameEn ?? card.name) : card.name;
}

/**
 * **双语牌名**：`中文 / English`（en 界面只给英文）。
 *
 * ⚠️ lore 里引用牌名**不要**用它 —— 说明正文要干净的单语名，见 `loreToTagged`。
 */
export function cardBilingual(card: CardDef): string {
  if (getLang() === 'en') return cardName(card);
  return card.name + ' / ' + (card.nameEn ?? card.name);
}

/** 显示用牌面短文案：en 取 `descriptionEn`，缺省回退中文 `description` */
export function cardDescription(card: CardDef): string {
  return getLang() === 'en' ? (card.descriptionEn ?? card.description) : card.description;
}

/** 显示用悬停详细说明（结构化）：en 取 `loreEn`，缺省回退中文 `lore` */
export function cardLore(card: CardDef): Lore {
  return getLang() === 'en' ? (card.loreEn ?? card.lore) : card.lore;
}

/** 只要一个 `get(id)` 就够 —— 牌注册表 / Map 都满足 */
export interface CardLookup {
  get(id: CardTitle): CardDef | undefined;
}

/**
 * **lore 里的牌引用怎么取显示名。**
 *
 * 给 `CardTooltip` / `loreToTagged` 用：查不到就把包前缀去掉、显示 id 尾段，
 * 而不是抛异常（说明面板不该因为一条坏引用整块消失）。
 */
export function cardNameResolver(cards: CardLookup): (id: CardTitle) => string {
  return (id) => {
    const card = cards.get(id);
    return card === undefined ? String(id).replace(/^[^:]+:/, '') : cardName(card);
  };
}
